// Modal de carga masiva de documentación (seguros, VTV, etc.) para varios
// equipos a la vez. Pensado para el caso típico de flota: el seguro se
// contrata en bloque y vence en la misma fecha para todos los equipos, así
// que renovarlo uno por uno sería muy tedioso.
//
// El usuario filtra/selecciona equipos, elige el tipo de documentación y
// carga fechas/notas una sola vez. El backend (documentacion/cargar-masivo/)
// sobreescribe el documento existente de ese tipo por equipo, o lo crea si
// todavía no lo tiene.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal.jsx';
import apiClient from '../../services/apiClient';
import { useToast } from '../common/Toast';
import SearchableSelectField from '../common/SearchableSelectField';
import { formatDateDisplay, parseDateLocal, toLocalYMD } from '../../utils/dateUtils';

const Subtitle = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.txtSecundario};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) auto;
  gap: 0.6rem;
  align-items: end;

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldLabel = styled.label`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.82rem;
  min-height: 32px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.82rem;
  min-height: 56px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BtnLimpiar = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 0.78rem;
  cursor: pointer;
  min-height: 32px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const SelectAllLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  cursor: pointer;
  user-select: none;
`;

const Check = styled.input.attrs({ type: 'checkbox' })`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const CountInfo = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const TableWrap = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  max-height: 320px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  text-align: left;
  padding: 0.5rem 0.6rem;
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtSecundario};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const Tr = styled.tr`
  cursor: pointer;
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primaryAlpha : 'transparent')};

  &:hover {
    background: ${({ $selected, theme }) => ($selected ? theme.colors.primaryAlpha : theme.colors.hoverRow)};
  }
`;

const EmptyRow = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 0.85rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $variant, theme }) => {
    if ($variant === 'danger') return theme.colors.dangerBg;
    if ($variant === 'success') return theme.colors.successBg;
    return theme.colors.infoBg;
  }};
  color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return theme.colors.dangerTxt;
    if ($variant === 'success') return theme.colors.successTxt;
    return theme.colors.infoTxt;
  }};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CheckBoolWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  cursor: pointer;
  padding-top: 1.3rem;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const Footer = styled.div`
  position: sticky;
  bottom: -1rem;
  margin: 0 -1rem -1rem;
  padding: 0.9rem 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const BtnSecondary = styled.button`
  padding: 0.45rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.85rem;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnPrimary = styled.button`
  padding: 0.45rem 1.1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Trae todas las páginas de un catálogo paginado por DRF.
async function fetchAll(endpoint, params = {}) {
  try {
    const resp = await apiClient.get(endpoint, { params: { page_size: 10000, ...params } });
    const data = resp?.data;
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.results) ? data.results : [];
  } catch (e) {
    console.error('[CargaMasivaDocumentacion] fetchAll', endpoint, e);
    return [];
  }
}

export default function ModalCargaMasivaDocumentacion({ onClose, onSaved }) {
  const { toast } = useToast();

  const [cat, setCat] = useState({ familias: [], tipos: [], sucursales: [], estados: [], tiposDoc: [] });
  const [cargandoCat, setCargandoCat] = useState(true);

  const [filtros, setFiltros] = useState({ familia: '', tipo: '', sucursal: '', estado: '', search: '' });
  const [equipos, setEquipos] = useState([]);
  const [cargandoEquipos, setCargandoEquipos] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [tipoDoc, setTipoDoc] = useState('');
  const [existentes, setExistentes] = useState({}); // { equipoId: { fecha_vencimiento, no_vence } }
  const [cargandoExistentes, setCargandoExistentes] = useState(false);

  const [fechaEmision, setFechaEmision] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [noVence, setNoVence] = useState(false);
  const [notas, setNotas] = useState('');
  const [esPublico, setEsPublico] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [familias, tipos, sucursales, estados, tiposDoc] = await Promise.all([
        fetchAll('familias/'), fetchAll('tipos/'), fetchAll('sucursales/'),
        fetchAll('estados/'), fetchAll('tiposdocumentacion/'),
      ]);
      if (!vivo) return;
      setCat({
        familias: familias.filter(Boolean), tipos: tipos.filter(Boolean),
        sucursales: sucursales.filter(Boolean), estados: estados.filter(Boolean),
        tiposDoc: tiposDoc.filter(Boolean),
      });
      setCargandoCat(false);
    })();
    return () => { vivo = false; };
  }, []);

  const buscarEquipos = async (filtrosActuales) => {
    setCargandoEquipos(true);
    try {
      const params = { lite: true, ordering: 'interno' };
      if (filtrosActuales.familia) params.familia = filtrosActuales.familia;
      if (filtrosActuales.tipo) params.tipo = filtrosActuales.tipo;
      if (filtrosActuales.sucursal) params.sucursal = filtrosActuales.sucursal;
      if (filtrosActuales.estado) params.estado = filtrosActuales.estado;
      if (filtrosActuales.search) params.search = filtrosActuales.search;
      const resp = await apiClient.get('equipos/', { params });
      const data = resp?.data?.results || resp?.data || [];
      setEquipos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('[CargaMasivaDocumentacion] buscarEquipos', e);
      toast.error('No se pudieron cargar los equipos.');
    } finally {
      setCargandoEquipos(false);
    }
  };

  // Recarga la lista de equipos cuando cambian los filtros de catálogo
  // (selects); el texto libre se busca con Enter o el botón para no pegarle
  // a la API en cada tecla.
  useEffect(() => {
    buscarEquipos(filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.familia, filtros.tipo, filtros.sucursal, filtros.estado]);

  useEffect(() => {
    let vivo = true;
    if (!tipoDoc) {
      setExistentes({});
      return;
    }
    setCargandoExistentes(true);
    (async () => {
      const docs = await fetchAll('documentacion/', { tipo__id: tipoDoc });
      if (!vivo) return;
      const map = {};
      docs.forEach((d) => {
        const equipoId = typeof d.equipo === 'object' ? d.equipo?.id : d.equipo;
        if (equipoId != null) map[equipoId] = { fecha_vencimiento: d.fecha_vencimiento, no_vence: d.no_vence };
      });
      setExistentes(map);
      setCargandoExistentes(false);
    })();
    return () => { vivo = false; };
  }, [tipoDoc]);

  const setFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visiblesSeleccionados = useMemo(
    () => equipos.length > 0 && equipos.every((eq) => selectedIds.has(eq.id)),
    [equipos, selectedIds]
  );

  const toggleSeleccionarVisibles = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (visiblesSeleccionados) {
        equipos.forEach((eq) => next.delete(eq.id));
      } else {
        equipos.forEach((eq) => next.add(eq.id));
      }
      return next;
    });
  };

  const limpiarSeleccion = () => setSelectedIds(new Set());

  const renderEstadoDoc = (equipoId) => {
    if (!tipoDoc) return <span>-</span>;
    if (cargandoExistentes) return <span>…</span>;
    const existente = existentes[equipoId];
    if (!existente) return <Badge $variant="info">Se creará</Badge>;
    if (existente.no_vence) return <Badge $variant="success">No vence</Badge>;
    const vencido = existente.fecha_vencimiento
      && parseDateLocal(existente.fecha_vencimiento) < parseDateLocal(toLocalYMD(new Date()));
    return (
      <Badge $variant={vencido ? 'danger' : 'success'}>
        Vence {formatDateDisplay(existente.fecha_vencimiento)}
      </Badge>
    );
  };

  const guardar = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleccioná al menos un equipo.');
      return;
    }
    if (!tipoDoc) {
      toast.error('Elegí el tipo de documentación.');
      return;
    }
    if (!noVence && !fechaVencimiento) {
      toast.error('La fecha de vencimiento es obligatoria (o marcá "No vence").');
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        equipos: Array.from(selectedIds),
        tipo: tipoDoc,
        fecha_emision: fechaEmision || null,
        fecha_vencimiento: noVence ? null : (fechaVencimiento || null),
        no_vence: noVence,
        notas,
        es_publico: esPublico,
      };
      const resp = await apiClient.post('documentacion/cargar-masivo/', payload);
      const data = resp?.data || {};
      toast.success(data.detail || 'Documentación actualizada.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.response?.data && typeof e.response.data === 'object'
          ? Object.values(e.response.data).flat().join(' ')
          : null)
        || 'No se pudo guardar la documentación.';
      toast.error(detail);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Carga masiva de documentación" size="xxl">
      <Subtitle>
        Filtrá y seleccioná los equipos, elegí el tipo de documento y cargá una sola vez la
        fecha de emisión, vencimiento y notas. Se aplica a todos los equipos tildados: si ya
        tenían un documento de ese tipo se actualiza, si no, se crea.
      </Subtitle>

      <Section>
        <SectionTitle><i className="fa fa-truck" /> Equipos</SectionTitle>

        {cargandoCat ? <span>Cargando catálogos…</span> : (
          <FiltersBar>
            <FilterField>
              <FieldLabel>Familia</FieldLabel>
              <SearchableSelectField
                options={cat.familias}
                value={filtros.familia}
                onChange={(val) => setFiltro('familia', val)}
                placeholder="Todas"
              />
            </FilterField>
            <FilterField>
              <FieldLabel>Tipo de equipo</FieldLabel>
              <SearchableSelectField
                options={cat.tipos}
                value={filtros.tipo}
                onChange={(val) => setFiltro('tipo', val)}
                placeholder="Todos"
              />
            </FilterField>
            <FilterField>
              <FieldLabel>Sucursal</FieldLabel>
              <SearchableSelectField
                options={cat.sucursales}
                value={filtros.sucursal}
                onChange={(val) => setFiltro('sucursal', val)}
                placeholder="Todas"
              />
            </FilterField>
            <FilterField>
              <FieldLabel>Estado</FieldLabel>
              <SearchableSelectField
                options={cat.estados}
                value={filtros.estado}
                onChange={(val) => setFiltro('estado', val)}
                placeholder="Todos"
              />
            </FilterField>
            <FilterField>
              <FieldLabel>Buscar</FieldLabel>
              <TextInput
                type="text"
                placeholder="Interno, patente…"
                value={filtros.search}
                onChange={(e) => setFiltro('search', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') buscarEquipos(filtros); }}
                onBlur={() => buscarEquipos(filtros)}
              />
            </FilterField>
          </FiltersBar>
        )}

        <TableToolbar>
          <SelectAllLabel>
            <Check
              checked={visiblesSeleccionados}
              onChange={toggleSeleccionarVisibles}
              disabled={equipos.length === 0}
            />
            Seleccionar todos los filtrados ({equipos.length})
          </SelectAllLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <CountInfo>{selectedIds.size} equipo{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}</CountInfo>
            {selectedIds.size > 0 && <BtnLimpiar type="button" onClick={limpiarSeleccion}>Limpiar selección</BtnLimpiar>}
          </div>
        </TableToolbar>

        <TableWrap>
          {cargandoEquipos ? (
            <EmptyRow>Cargando equipos…</EmptyRow>
          ) : equipos.length === 0 ? (
            <EmptyRow>No hay equipos para estos filtros.</EmptyRow>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 32 }} />
                  <Th>Interno</Th>
                  <Th>Descripción</Th>
                  <Th>Familia</Th>
                  <Th>Sucursal</Th>
                  <Th>Estado</Th>
                  <Th>Documento {tipoDoc ? '' : '(elegí el tipo abajo)'}</Th>
                </tr>
              </thead>
              <tbody>
                {equipos.map((eq) => (
                  <Tr key={eq.id} $selected={selectedIds.has(eq.id)} onClick={() => toggleSelect(eq.id)}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Check checked={selectedIds.has(eq.id)} onChange={() => toggleSelect(eq.id)} />
                    </Td>
                    <Td>{eq.interno}</Td>
                    <Td>{eq.descripcion}</Td>
                    <Td>{eq.familia_nombre}</Td>
                    <Td>{eq.sucursal_nombre}</Td>
                    <Td>{eq.estado_nombre}</Td>
                    <Td>{renderEstadoDoc(eq.id)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableWrap>
      </Section>

      <Section>
        <SectionTitle><i className="fa fa-file-alt" /> Datos del documento</SectionTitle>
        <FormGrid>
          <FilterField>
            <FieldLabel>Tipo de documentación *</FieldLabel>
            <SearchableSelectField
              options={cat.tiposDoc}
              value={tipoDoc}
              onChange={setTipoDoc}
              placeholder="Buscar tipo…"
            />
          </FilterField>
          <div />
          <FilterField>
            <FieldLabel>Fecha de emisión</FieldLabel>
            <TextInput type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} />
          </FilterField>
          <FilterField>
            <FieldLabel>Fecha de vencimiento {noVence ? '' : '*'}</FieldLabel>
            <TextInput
              type="date"
              value={fechaVencimiento}
              onChange={(e) => setFechaVencimiento(e.target.value)}
              disabled={noVence}
            />
          </FilterField>
          <CheckBoolWrap>
            <Check checked={noVence} onChange={(e) => setNoVence(e.target.checked)} />
            Este documento no vence
          </CheckBoolWrap>
          <CheckBoolWrap>
            <Check checked={esPublico} onChange={(e) => setEsPublico(e.target.checked)} />
            Visible en portal QR (sin login)
          </CheckBoolWrap>
          <FullRow>
            <FilterField>
              <FieldLabel>Notas</FieldLabel>
              <TextArea value={notas} onChange={(e) => setNotas(e.target.value)} />
            </FilterField>
          </FullRow>
        </FormGrid>
      </Section>

      <Footer>
        <span />
        <Actions>
          <BtnSecondary type="button" onClick={onClose} disabled={guardando}>Cancelar</BtnSecondary>
          <BtnPrimary type="button" onClick={guardar} disabled={guardando || selectedIds.size === 0}>
            {guardando ? 'Guardando…' : `Aplicar a ${selectedIds.size} equipo${selectedIds.size !== 1 ? 's' : ''}`}
          </BtnPrimary>
        </Actions>
      </Footer>
    </Modal>
  );
}
